import React, { useState, useEffect } from "react";
import { connect } from "react-redux";
import Core from "class/Core";
import { Card, Form, Row, Col, Select, DatePicker, Button, Table, Typography, Space, Spin, message } from "antd";
import * as Icons from "@ant-design/icons";
import dayjs from "dayjs";
import "dayjs/locale/pt-br";
import ptBR from "antd/es/date-picker/locale/pt_BR";
import { useMutation, useQuery } from "react-query";

dayjs.locale("pt-br");

const TrabalhadorPrestadores = (props) => {
  const { Title, Text } = Typography;
  const { RangePicker } = DatePicker;
  const [form] = Form.useForm();

  // Instanciar Core
  const core = new Core();

  // States
  const [tableData, setTableData] = useState([]);
  const [showTable, setShowTable] = useState(false);
  const [formValid, setFormValid] = useState(false);
  const [qtdTotal, setQtdTotal] = useState(0);

  // Verificar autenticação ao montar componente
  useEffect(() => {
    core.isAuth();
  }, []);

  // Query para buscar favorecidos
  const { data: favorecidos = [], isLoading: loadingFavorecidos } = useQuery(
    "FAVORECIDOS-TRABALHADORES",
    async () => {
      const response = await fetch(
        `${core.getUrl()}/api-v1/empresa/relatorios/relacao-assistencia`,
        {
          method: "GET",
          headers: core.getHeadersFetch(true),
        }
      );

      const result = await core.responseConvert(response);

      return result.content.map((el) => ({
        value: el.enti_cd_nseq,
        label: el.enti_ds_sigla,
      }));
    },
    {
      onError: (err) => {
        core.catchFecth(err);
      }
    }
  );

  // Mutation para buscar dados
  const { mutate: executeBusca, isLoading: loadingBusca } = useMutation(
    async (params) => {
      // Validação antes do envio
      if (!params.favorecido) {
        throw new Error('Parâmetro favorecido é obrigatório');
      }

      // Construir query params com token
      const queryParams = new URLSearchParams({
        date_ini: params.date_ini,
        date_fim: params.date_fim,
        favorecido: params.favorecido,
        excel: params.excel,
        token: core.getToken()
      });

      const response = await fetch(
        `${core.getUrl()}/api-v1/empresa/relatorios/excel-trabalhador-assistencia?${queryParams}`,
        {
            method: "POST",
            headers: {
                'Accept': 'application/json, text/plain, */*',
                'Content-Type': 'application/json'
            }
        }
      );

      return await core.responseConvert(response);
    },
    {
      onSuccess: (result) => {
        const dados = result.content || [];
        setTableData(dados);
        setShowTable(true);

        const qtd1 = result.resumo?.total_trabalhadores_mes || 0;
        const qtd2 = result.resumo?.total_trabalhadores_acordos || 0;
        setQtdTotal(qtd1 + qtd2);

        message.success(`${dados.length} registros encontrados`);
      },
      onError: (err) => {
        core.catchFecth(err);
      }
    }
  );

  // Handler de submit (Solução 3 - Validação complementar)
  const onFinish = (values) => {
    // Validação de dados antes do envio
    if (!values.dateRange || values.dateRange.length !== 2) {
      message.error('Período inválido');
      return;
    }

    if (!values.favorecido) {
      message.error('Favorecido não selecionado');
      return;
    }

    const params = {
      date_ini: values.dateRange[0].format("DD/MM/YYYY"),
      date_fim: values.dateRange[1].format("DD/MM/YYYY"),
      favorecido: values.favorecido,
      excel: "nao",
    };

    executeBusca(params);
  };

  // Handler para download Excel
  const onDownloadExcel = () => {
    // Verificar permissão (opcional)
    if (!core.hasUrlPermission('/manager/relatorios-entidade/trabalhadores')) {
      message.warning('Você não tem permissão para exportar este relatório');
      return;
    }

    const values = form.getFieldsValue();
    const params = new URLSearchParams({
      date_ini: values.dateRange[0].format("DD/MM/YYYY"),
      date_fim: values.dateRange[1].format("DD/MM/YYYY"),
      favorecido: values.favorecido,
      excel: "sim",
      token: core.getToken()
    });

    const url = `${core.getUrl()}/api-v1/empresa/relatorios/excel-trabalhador-assistencia?${params}`;
    window.location.href = url;
    message.success("Gerando Excel...");
  };

  // Colunas da tabela
  const columns = [
    { title: "Nome", dataIndex: "nome", key: "nome", width: 250, ellipsis: true, fixed: "left" },
    { title: "CPF", dataIndex: "cpf", key: "cpf", width: 120 },
    { title: "Nascimento", dataIndex: "dt_nascimento", key: "dt_nascimento", width: 120 },
    { title: "Sexo", dataIndex: "sexo", key: "sexo", width: 80 },
    { title: "Admissão", dataIndex: "dt_admissao", key: "dt_admissao", width: 120 },
    { title: "Inclusão", dataIndex: "dt_inclusao", key: "dt_inclusao", width: 120 },
    { title: "Empresa", dataIndex: "razao_social", key: "razao_social", width: 250, ellipsis: true },
    { title: "CNPJ", dataIndex: "cnpj", key: "cnpj", width: 140 },
    { title: "Plano", dataIndex: "placa", key: "placa", width: 80 },
    {
      title: "Dt Vcto",
      dataIndex: "data_vencimento",
      key: "data_vencimento",
      width: 120,
      render: (text) => text ? dayjs(text).format("DD/MM/YYYY") : "-"
    },
    {
      title: "Dt Pgto",
      dataIndex: "data_pagamento",
      key: "data_pagamento",
      width: 120,
      render: (text) => text ? dayjs(text).format("DD/MM/YYYY") : "-"
    },
    { title: "N.Numero", dataIndex: "nosso_numero", key: "nosso_numero", width: 120 },
    { title: "Situação", dataIndex: "situacao", key: "situacao", width: 100 },
  ];

  const loading = loadingFavorecidos || loadingBusca;

  return (
    <>
      {/* Header */}
      <Card style={{ marginBottom: 24 }} bodyStyle={{ padding: "20px 24px" }}>
        <Row align="middle" justify="space-between">
          <Col>
            <Title level={2} style={{ margin: 0, color: "#1890ff" }}>
              <Icons.UserOutlined style={{ marginRight: 8 }} />
              Relação de Trabalhador - Pagamento
            </Title>
            <Text type="secondary" style={{ fontSize: "14px" }}>
              Consulte os trabalhadores cadastrados nas empresas no Sistema BSFOnline
              e que constam em boletos pagos pelas empresas
            </Text>
          </Col>
        </Row>
      </Card>

      {/* Filtros */}
      <Card
        title={
          <Space>
            <Icons.SearchOutlined />
            <span>Filtros de Pesquisa</span>
          </Space>
        }
        style={{ marginBottom: 24 }}
        bodyStyle={{ padding: "20px 24px" }}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={onFinish}
          onValuesChange={(changedValues, allValues) => {
            const { dateRange, favorecido } = allValues;
            setFormValid(
              dateRange && dateRange.length === 2 && favorecido
            );
          }}
        >
          <Row gutter={[16, 16]}>
            <Col xs={24} sm={12} md={8} lg={8}>
              <Form.Item
                name="dateRange"
                label={
                  <Space>
                    <Icons.CalendarOutlined />
                    <span>Período de Pagamento</span>
                  </Space>
                }
                rules={[{ required: true, message: "Selecione o período" }]}
              >
                <RangePicker
                  locale={ptBR}
                  format="DD/MM/YYYY"
                  placeholder={["Data inicial", "Data final"]}
                  style={{ width: "100%" }}
                  size="large"
                  separator="até"
                />
              </Form.Item>
            </Col>

            <Col xs={24} sm={12} md={8} lg={8}>
              <Form.Item
                name="favorecido"
                label={
                  <Space>
                    <Icons.BankOutlined />
                    <span>Favorecido</span>
                  </Space>
                }
                rules={[{ required: true, message: "Selecione o favorecido" }]}
              >
                <Select
                  showSearch
                  placeholder="Selecione o favorecido"
                  options={favorecidos}
                  filterOption={(input, option) =>
                    (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
                  }
                  size="large"
                  loading={loadingFavorecidos}
                />
              </Form.Item>
            </Col>

            <Col xs={24} sm={12} md={8} lg={8}>
              <Form.Item label=" " colon={false}>
                <Space direction="vertical" style={{ width: "100%" }} size="middle">
                  <Button
                    type="primary"
                    htmlType="submit"
                    size="large"
                    disabled={!formValid || loading}
                    loading={loading}
                    icon={<Icons.SearchOutlined />}
                    style={{ width: "100%" }}
                  >
                    BUSCAR
                  </Button>
                  <Button
                    size="large"
                    disabled={!showTable || loading}
                    icon={<Icons.FileExcelOutlined />}
                    style={{ width: "100%" }}
                    onClick={onDownloadExcel}
                  >
                    EXCEL
                  </Button>
                </Space>
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Card>

      {/* Total */}
      {showTable && (
        <Card style={{ marginBottom: 16 }}>
          <Text strong>Total de Trabalhadores: </Text>
          <Text style={{ fontSize: 18, color: "#1890ff" }}>{qtdTotal.toLocaleString("de-DE")}</Text>
        </Card>
      )}

      {/* Tabela */}
      {showTable && (
        <Card>
          <Spin spinning={loading}>
            <Table
              columns={columns}
              dataSource={tableData}
              rowKey={(record, index) => `row-${index}`}
              pagination={{
                pageSize: 50,
                hideOnSinglePage: true,
                showSizeChanger: false,
              }}
              scroll={{ x: 1400 }}
              locale={{
                emptyText: (
                  <div style={{ textAlign: "center" }}>
                    <b>Informe os parâmetros necessários para a pesquisa.</b>
                  </div>
                ),
              }}
            />
          </Spin>
        </Card>
      )}

      {/* Empty state */}
      {!showTable && !loading && (
        <Card style={{ textAlign: "center", padding: "40px 20px" }}>
          <div style={{ color: "#999" }}>
            <Icons.SearchOutlined style={{ fontSize: "48px", marginBottom: "16px" }} />
            <Title level={4} type="secondary">
              Configure os filtros e clique em "BUSCAR"
            </Title>
            <Text type="secondary">
              Selecione o período de pagamento e o favorecido para visualizar os trabalhadores
            </Text>
          </div>
        </Card>
      )}
    </>
  );
};

const mapStateToProps = (state) => {
  return {
    user: state.user,
  };
};

export default connect(mapStateToProps)(TrabalhadorPrestadores);
